import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
} from "recharts";

import { KpiCard } from "../../../Component/OperationComponents/KpiCard";
import { SectionCard } from "../../../Component/OperationComponents/SectionCard";
import { StatusRow } from "../../../Component/OperationComponents/StatusRow";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { BASE_URL } from "../../../config/api";
import { ValueLoader } from "../../../Component/Common/ValueLoader";
import DatePicker from "react-datepicker";

/* ─── Inline skeleton loader ─── */
const InlineLoader = ({ width = "w-8" }) => (
  <span className={`inline-block align-middle ${width}`}>
    <ValueLoader />
  </span>
);

/* ─── Stat card used in the hero row ─── */
const HeroStatCard = ({ label, value, sub, accent = "#4B6EF5", onClick, icon }) => (
  <div
    onClick={onClick}
    className={`relative overflow-hidden rounded-2xl p-3 flex flex-col gap-1 ${
      onClick ? "cursor-pointer" : ""
    }`}
    style={{ background: "#fff", boxShadow: "0 2px 16px 0 rgba(75,110,245,0.07)" }}
  >
    {/* accent bar */}
    <div
      className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl"
      style={{ background: accent }}
    />
    <div className="flex items-center justify-between mb-1">
      <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">{label}</span>
      {icon && (
        <span
          className="w-8 h-8 rounded-xl flex items-center justify-center text-white text-base"
          style={{ background: accent }}
        >
          {icon}
        </span>
      )}
    </div>
    <div className="text-xl font-bold text-slate-800">{value}</div>
    {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
  </div>
);

/* ─── Follow-up tile ─── */
const FollowTile = ({ label, value, bg, text, border }) => (
  <div className={`rounded-2xl ${bg} ${border} border p-2 flex flex-col`}>
    <span className="text-xs text-slate-500">{label}</span>
    <span className={`text-xl font-bold ${text}`}>{value}</span>
  </div>
);

/* ─── Retailer progress row ─── */
const RetailerRow = ({ label, count, total }) => {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="flex flex-col gap-1">
      <div className="flex justify-between text-xs text-slate-500">
        <span>{label}</span>
        <span className="font-semibold text-slate-700">
          {count} / {total}
          <span className="ml-1 text-slate-400">({pct}%)</span>
        </span>
      </div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{
            width: `${pct}%`,
            background: "linear-gradient(90deg, #4B6EF5, #7C9FF7)",
          }}
        />
      </div>
    </div>
  );
};

/* ─── Custom tooltip for chart ─── */
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className="rounded-xl px-4 py-3 text-xs shadow-lg"
        style={{ background: "#fff", border: "1px solid #e8eaf6" }}
      >
        <p className="font-semibold text-slate-600 mb-2">{label}</p>
        {payload.map((p) => (
          <div key={p.dataKey} className="flex items-center gap-2 mb-1">
            <span className="w-2 h-2 rounded-full" style={{ background: p.fill }} />
            <span className="text-slate-500">{p.name}:</span>
            <span className="font-bold text-slate-700">₹{p.value?.toLocaleString("en-IN")}</span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

export default function DashboardView({
  salesTrend,
  retailers,
  tasks,
  setCurrentView,
}) {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);

  const [kpis, setKpis] = useState({
    totalAssigned: 0,
    connected: 0,
    notConnected: 0,
    usingProduct: 0,
    notUsingProduct: 0,
    monthSales: 0,
    outstanding: 0,
  });

  const [followupStats, setFollowupStats] = useState({
    today: 0,
    tomorrow: 0,
    pending: 0,
    upcoming: 0,
  });

  const [retailerLineStats, setRetailerLineStats] = useState({
    active: { count: 0, total: 0, percent: 0 },
    connected: { count: 0, total: 0, percent: 0 },
    using: { count: 0, total: 0, percent: 0 },
  });

  const [salesChartType, setSalesChartType] = useState("day");
  const [salesChartData, setSalesChartData] = useState([]);
  const [salesChartLoading, setSalesChartLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(null);

  const authUser = JSON.parse(localStorage.getItem("authUserData"));
  const locationMode = authUser?.location_mode;
  const userState = authUser?.state;
  const userDistrict = authUser?.district;

  const getMonthRange = (date) => {
    const from = new Date(date.getFullYear(), date.getMonth(), 1);
    const to = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    return {
      from_date: from.toISOString().split("T")[0],
      to_date: to.toISOString().split("T")[0],
    };
  };

  const formatDayLabel = (value) => {
    const date = new Date(value);
    return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
  };

  const formatMonthLabel = (value) => {
    const date = new Date(value);
    return date.toLocaleDateString("en-IN", { month: "short" });
  };

  const getAllDatesOfMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    return Array.from({ length: daysInMonth }, (_, i) => {
      const d = new Date(year, month, i + 1);
      return d.toISOString().split("T")[0];
    });
  };

  const getAllMonthsOfYear = (year) => {
    return Array.from({ length: 12 }, (_, i) => {
      const d = new Date(year, i, 1);
      return d.toISOString().slice(0, 7);
    });
  };

  const fetchEmployeeDashboard = async () => {
    try {
      setLoading(true);
      const params = {};
      if (locationMode === "state") params.state = userState;
      if (locationMode === "district") params.district = userDistrict;
      const res = await axios.get(`${BASE_URL}company/employee-dashboard`, { params });
      if (res.data?.success) {
        const d = res.data;
        setKpis({
          totalAssigned: d.assigned_retailers,
          connected: d.connected_retailers.connected,
          notConnected: d.connected_retailers.not_connected,
          usingProduct: d.using_product.using,
          notUsingProduct: d.using_product.not_using,
          monthSales: d.sales.this_month,
          outstanding: d.sales.outstanding,
        });
        setFollowupStats({
          today: d.lead_followups.today,
          tomorrow: d.lead_followups.tomorrow,
          pending: d.lead_followups.pending,
          upcoming: d.lead_followups.upcoming,
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRetailerLineChart = async () => {
    try {
      const params = {};
      if (locationMode === "state") params.state = userState;
      if (locationMode === "district") params.district = userDistrict;
      const res = await axios.get(`${BASE_URL}company/employee-retailer-line-chart`, { params });
      if (res.data?.success) setRetailerLineStats(res.data.data);
    } catch (err) {
      console.error("Line chart API failed", err);
    }
  };

  const fetchSalesChart = async () => {
    try {
      setSalesChartLoading(true);
      const params = { type: salesChartType };
      if (salesChartType === "day" && selectedMonth) {
        const { from_date, to_date } = getMonthRange(selectedMonth);
        params.from_date = from_date;
        params.to_date = to_date;
      }
      if (locationMode === "state" && userState) params.states = userState;
      if (locationMode === "district" && userDistrict) params.districts = userDistrict;
      const res = await axios.get(`${BASE_URL}company/employee-sales-chart`, { params });
      if (res.data?.success) {
        const { labels = [], datasets = [] } = res.data.chart;
        let formatted = [];
        if (salesChartType === "day") {
          const baseMonth = selectedMonth || new Date();
          const allDates = getAllDatesOfMonth(baseMonth);
          const apiDataMap = {};
          labels.forEach((label, index) => {
            apiDataMap[label] = {
              sales: datasets[0]?.data?.[index] || 0,
              collection: datasets[1]?.data?.[index] || 0,
            };
          });
          formatted = allDates.map((date) => ({
            label: formatDayLabel(date),
            sales: apiDataMap[date]?.sales || 0,
            collection: apiDataMap[date]?.collection || 0,
          }));
        }
        if (salesChartType === "month") {
          const allMonths = getAllMonthsOfYear(new Date().getFullYear());
          const apiDataMap = {};
          labels.forEach((label, index) => {
            apiDataMap[label] = {
              sales: datasets[0]?.data?.[index] || 0,
              collection: datasets[1]?.data?.[index] || 0,
            };
          });
          formatted = allMonths.map((month) => ({
            label: formatMonthLabel(month),
            sales: apiDataMap[month]?.sales || 0,
            collection: apiDataMap[month]?.collection || 0,
          }));
        }
        setSalesChartData(formatted);
      }
    } catch (err) {
      console.error("Sales chart API failed", err);
    } finally {
      setSalesChartLoading(false);
    }
  };

  useEffect(() => {
    if (!locationMode) return;
    fetchEmployeeDashboard();
    fetchRetailerLineChart();
    fetchSalesChart();
  }, [locationMode, userState, userDistrict, salesChartType, selectedMonth]);

  /* ── Derived quick stat ── */
  const connectionPct =
    kpis.totalAssigned > 0
      ? Math.round((kpis.connected / kpis.totalAssigned) * 100)
      : 0;

  return (
    <div
      className="space-y-3"
      style={{ background: "#F2F4FC", fontFamily: "'Inter', sans-serif" }}
    >
      {/* ═══════════════ HERO BANNER ═══════════════ */}
      {/* <div
        className="rounded-3xl p-6 flex flex-col md:flex-row md:items-center justify-between gap-4"
        style={{
          background: "linear-gradient(120deg, #4B6EF5 0%, #7C9FF7 60%, #B3C8FF 100%)",
          boxShadow: "0 8px 32px 0 rgba(75,110,245,0.18)",
        }}
      >
        <div>
          <p className="text-blue-100 text-sm font-medium mb-1 uppercase tracking-widest">
            Operations Dashboard
          </p>
          <h1 className="text-white text-2xl font-bold leading-tight">
            Retailer Performance Overview
          </h1>
          <p className="text-blue-200 text-sm mt-1">
            {new Date().toLocaleDateString("en-IN", {
              weekday: "long",
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </p>
        </div>

        
        <div className="flex gap-3 flex-wrap">
          <div
            className="rounded-2xl px-5 py-3 flex flex-col items-center"
            style={{ background: "rgba(255,255,255,0.18)", backdropFilter: "blur(8px)" }}
          >
            <span className="text-white text-xl font-bold">
              {loading ? "—" : kpis.totalAssigned}
            </span>
            <span className="text-blue-100 text-xs mt-0.5">Assigned</span>
          </div>
          <div
            className="rounded-2xl px-5 py-3 flex flex-col items-center"
            style={{ background: "rgba(255,255,255,0.18)", backdropFilter: "blur(8px)" }}
          >
            <span className="text-white text-xl font-bold">
              {loading ? "—" : `${connectionPct}%`}
            </span>
            <span className="text-blue-100 text-xs mt-0.5">Connected</span>
          </div>
          <div
            className="rounded-2xl px-5 py-3 flex flex-col items-center"
            style={{ background: "rgba(255,255,255,0.18)", backdropFilter: "blur(8px)" }}
          >
            <span className="text-white text-xl font-bold">
              {loading ? "—" : `₹${(kpis.monthSales / 1000).toFixed(1)}k`}
            </span>
            <span className="text-blue-100 text-xs mt-0.5">This Month</span>
          </div>
        </div>
      </div> */}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <HeroStatCard
          label="Assigned Retailers"
          value={loading ? <InlineLoader /> : kpis.totalAssigned}
          icon="🏪"
          accent="#4B6EF5"
          onClick={() => navigate("/oprretail", { state: { filter: "assigned" } })}
        />
        <HeroStatCard
          label="Connected"
          value={
            loading
              ? <InlineLoader width="w-12" />
              : `${kpis.connected}/${kpis.totalAssigned}`
          }
          sub={loading ? null : `${kpis.notConnected} not connected`}
          icon="🔗"
          accent="#7C9FF7"
          onClick={() => navigate("/oprretail", { state: { filter: "connected" } })}
        />
        <HeroStatCard
          label="Using Product"
          value={loading ? <InlineLoader /> : kpis.usingProduct}
          sub={loading ? null : `${kpis.notUsingProduct} not using`}
          icon="📦"
          accent="#10b981"
          onClick={() => navigate("/oprretail", { state: { filter: "using" } })}
        />
        <HeroStatCard
          label="Month Sales"
          value={
            loading
              ? <InlineLoader width="w-14" />
              : `₹${(kpis.monthSales / 1000).toFixed(1)}k`
          }
          icon="📈"
          accent="#f59e0b"
        />
        <HeroStatCard
          label="Outstanding"
          value={
            loading
              ? <InlineLoader width="w-14" />
              : `₹${(kpis.outstanding / 1000).toFixed(1)}k`
          }
          sub="Follow up for collection"
          icon="⚠️"
          accent="#ef4444"
        />
      </div>

     {/* ═══════════════ MAIN GRID ═══════════════ */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        <div className="lg:col-span-2 flex flex-col gap-4">
        
          <div
            className="rounded-3xl p-3"
            style={{ background: "#fff", boxShadow: "0 2px 16px 0 rgba(75,110,245,0.07)" }}
          >
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-slate-700">Retailer Follow-ups</h3>
              <p className="text-xs text-slate-400">Lead follow-ups by timing</p>
            </div>
            <div className="grid grid-cols-4 gap-3">
              <FollowTile
                label="Today"
                value={followupStats.today}
                bg="bg-blue-50"
                border="border-blue-100"
                text="text-blue-600"
              />
              <FollowTile
                label="Tomorrow"
                value={followupStats.tomorrow}
                bg="bg-emerald-50"
                border="border-emerald-100"
                text="text-emerald-600"
              />
              <FollowTile
                label="Pending"
                value={followupStats.pending}
                bg="bg-amber-50"
                border="border-amber-100"
                text="text-amber-600"
              />
              <FollowTile
                label="Upcoming"
                value={followupStats.upcoming}
                bg="bg-violet-50"
                border="border-violet-100"
                text="text-violet-600"
              />
            </div>
          </div>

          {/* 2. Sales vs Collection Chart (Bottom Left) */}
          <div
            className="rounded-3xl p-3 flex-1"
            style={{ background: "#fff", boxShadow: "0 2px 16px 0 rgba(75,110,245,0.07)" }}
          >
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div>
                <h3 className="text-sm font-semibold text-slate-700">Sales vs Collection</h3>
                <p className="text-xs text-slate-400">Assigned retailers — current period</p>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                {salesChartType === "day" && (
                  <DatePicker
                    selected={selectedMonth}
                    onChange={(date) => setSelectedMonth(date)}
                    dateFormat="MMM yyyy"
                    showMonthYearPicker
                    placeholderText="Pick month"
                    className="border border-slate-200 rounded-xl px-3 py-1.5 text-xs cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-300"
                  />
                )}
                <div
                  className="flex rounded-xl overflow-hidden border border-slate-200"
                  style={{ background: "#f5f6ff" }}
                >
                  <button
                    onClick={() => setSalesChartType("day")}
                    className="px-4 py-1.5 text-xs font-medium transition-all"
                    style={
                      salesChartType === "day"
                        ? { background: "#4B6EF5", color: "#fff", borderRadius: "10px" }
                        : { color: "#64748b" }
                    }
                  >
                    Day
                  </button>
                  <button
                    onClick={() => setSalesChartType("month")}
                    className="px-4 py-1.5 text-xs font-medium transition-all"
                    style={
                      salesChartType === "month"
                        ? { background: "#4B6EF5", color: "#fff", borderRadius: "10px" }
                        : { color: "#64748b" }
                    }
                  >
                    Month
                  </button>
                </div>
              </div>
            </div>

            <div className="h-64">
              {salesChartLoading ? (
                <div className="h-full flex items-center justify-center">
                  <ValueLoader />
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={salesChartData} barCategoryGap="30%">
                    <defs>
                      <linearGradient id="salesGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#4B6EF5" />
                        <stop offset="100%" stopColor="#7C9FF7" />
                      </linearGradient>
                      <linearGradient id="collGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#10b981" />
                        <stop offset="100%" stopColor="#6ee7b7" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f2ff" vertical={false} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 11, fill: "#94a3b8" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: "#94a3b8" }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(v) => `₹${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`}
                    />
                    <Tooltip content={<CustomTooltip />} cursor={{ fill: "#f5f6ff" }} />
                    <Legend
                      wrapperStyle={{ fontSize: "11px", paddingTop: "12px" }}
                      iconType="circle"
                      iconSize={8}
                    />
                    <Bar dataKey="sales" fill="url(#salesGrad)" name="Total Sales" radius={[6, 6, 0, 0]} />
                    <Bar dataKey="collection" fill="url(#collGrad)" name="Paid Sales" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>

        
        <div className="lg:col-span-1 flex flex-col gap-4">
          {/* 3. Retailer Status Snapshot (Top Right) */}
          <div
            className="rounded-3xl p-3 flex flex-col gap-4"
            style={{ background: "#fff", boxShadow: "0 2px 16px 0 rgba(75,110,245,0.07)" }}
          >
            <div>
              <h3 className="text-sm font-semibold text-slate-700">Retailer Status</h3>
              <p className="text-xs text-slate-400">Snapshot of your assigned base</p>
            </div>
            <div className="space-y-4">
              <RetailerRow
                label="Active retailers"
                count={retailerLineStats.active.count}
                total={retailerLineStats.active.total}
              />
              <RetailerRow
                label="Connected retailers"
                count={retailerLineStats.connected.count}
                total={retailerLineStats.connected.total}
              />
              <RetailerRow
                label="Using product"
                count={retailerLineStats.using.count}
                total={retailerLineStats.using.total}
              />
            </div>
          </div>

          <div
            className="rounded-3xl p-3 flex-1"
            style={{ background: "#fff", boxShadow: "0 2px 16px 0 rgba(75,110,245,0.07)" }}
          >
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-slate-700">Lead Follow-ups</h3>
              <p className="text-xs text-slate-400">Timelines requiring your attention</p>
            </div>

            <div className="grid grid-cols-5 gap-3">
              
              <div className="col-span-2 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 p-4 flex flex-col justify-between">
                <span className="text-xs font-semibold text-blue-700 tracking-wide uppercase">Today</span>
                <div className="mt-4">
                  <span className="text-4xl font-extrabold text-blue-600 block">{followupStats.today}</span>
                  <span className="text-[10px] text-blue-400 font-medium">Due right now</span>
                </div>
              </div>

              
              <div className="col-span-3 flex flex-col gap-2">
                <div className="flex justify-between items-center bg-slate-50 rounded-xl p-2 px-3 border border-slate-100">
                  <span className="text-xs text-slate-500">Tomorrow</span>
                  <span className="text-sm font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-lg">
                    {followupStats.tomorrow}
                  </span>
                </div> 
                <div className="flex justify-between items-center bg-slate-50 rounded-xl p-2 px-3 border border-slate-100">
                  <span className="text-xs text-slate-500">Pending</span>
                  <span className="text-sm font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-lg">
                    {followupStats.pending}
                  </span>
                </div>
                <div className="flex justify-between items-center bg-slate-50 rounded-xl p-2 px-3 border border-slate-100">
                  <span className="text-xs text-slate-500">Upcoming</span>
                  <span className="text-sm font-bold text-violet-600 bg-violet-50 px-2 py-0.5 rounded-lg">
                    {followupStats.upcoming}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


// import {
//   BarChart,
//   Bar,
//   XAxis,
//   YAxis,
//   Tooltip,
//   CartesianGrid,
//   ResponsiveContainer,
//   Legend,
// } from "recharts";

// import { KpiCard } from "../../../Component/OperationComponents/KpiCard";
// import { SectionCard } from "../../../Component/OperationComponents/SectionCard";
// import { StatusRow } from "../../../Component/OperationComponents/StatusRow";
// import { useNavigate } from "react-router-dom";
// import { useEffect, useState } from "react";
// import axios from "axios";
// import { BASE_URL } from "../../../config/api";
// import { ValueLoader } from "../../../Component/Common/ValueLoader";
// import DatePicker from "react-datepicker";

// /* ---------- inline loader ---------- */
// const InlineLoader = ({ width = "w-8" }) => (
//   <span className={`inline-block align-middle ${width}`}>
//     <ValueLoader />
//   </span>
// );

// export default function DashboardView({
//   salesTrend,
//   retailers,
//   tasks,
//   setCurrentView,
// }) {
//   const navigate = useNavigate();

//   const [loading, setLoading] = useState(false);

//   const [kpis, setKpis] = useState({
//     totalAssigned: 0,
//     connected: 0,
//     notConnected: 0,
//     usingProduct: 0,
//     notUsingProduct: 0,
//     monthSales: 0,
//     outstanding: 0,
//   });

//   const [followupStats, setFollowupStats] = useState({
//   today: 0,
//   tomorrow: 0,
//   pending: 0,
//   upcoming: 0,
// });

//   const [retailerLineStats, setRetailerLineStats] = useState({
//     active: { count: 0, total: 0, percent: 0 },
//     connected: { count: 0, total: 0, percent: 0 },
//     using: { count: 0, total: 0, percent: 0 },
//   });

//   const [salesChartType, setSalesChartType] = useState("day"); // day | month
//   const [salesChartData, setSalesChartData] = useState([]); 
//   const [salesChartLoading, setSalesChartLoading] = useState(false);
//   const [selectedMonth, setSelectedMonth] = useState(null); // 👈 null by default

//   const authUser = JSON.parse(localStorage.getItem("authUserData"));
//   const locationMode = authUser?.location_mode;
//   const userState = authUser?.state;
//   const userDistrict = authUser?.district;

//   const getMonthRange = (date) => {
//     const from = new Date(date.getFullYear(), date.getMonth(), 1);
//     const to = new Date(date.getFullYear(), date.getMonth() + 1, 0);

//     return {
//       from_date: from.toISOString().split("T")[0],
//       to_date: to.toISOString().split("T")[0],
//     };
//   };

//   const formatDayLabel = (value) => {
//     // expects YYYY-MM-DD or DD-MM-YYYY
//     const date = new Date(value);
//     return date.toLocaleDateString("en-IN", {
//       day: "2-digit",
//       month: "short",
//     });
//   };

//   const formatMonthLabel = (value) => {
//   const date = new Date(value);
//   return date.toLocaleDateString("en-IN", { month: "short" });
// };


//   const getAllDatesOfMonth = (date) => {
//     const year = date.getFullYear();
//     const month = date.getMonth();

//     const daysInMonth = new Date(year, month + 1, 0).getDate();

//     return Array.from({ length: daysInMonth }, (_, i) => {
//       const d = new Date(year, month, i + 1);
//       return d.toISOString().split("T")[0]; // YYYY-MM-DD
//     });
//   };

//   const getLast12Months = () => {
//   return Array.from({ length: 12 }, (_, i) => {
//     const d = new Date();
//     d.setMonth(d.getMonth() - (11 - i));
//     return d.toISOString().slice(0, 7); // YYYY-MM
//   });
// };

// const getAllMonthsOfYear = (year) => {
//   return Array.from({ length: 12 }, (_, i) => {
//     const d = new Date(year, i, 1);
//     return d.toISOString().slice(0, 7); // YYYY-MM
//   });
// };



//   /* ================= FETCH KPI DASHBOARD ================= */
//   const fetchEmployeeDashboard = async () => {
//     try {
//       setLoading(true);

//       const params = {};
//       if (locationMode === "state") params.state = userState;
//       if (locationMode === "district") params.district = userDistrict;

//       const res = await axios.get(
//         `${BASE_URL}company/employee-dashboard`,
//         { params }
//       );

//       if (res.data?.success) {
//         const d = res.data;
//         setKpis({
//           totalAssigned: d.assigned_retailers,
//           connected: d.connected_retailers.connected,
//           notConnected: d.connected_retailers.not_connected,
//           usingProduct: d.using_product.using,
//           notUsingProduct: d.using_product.not_using,
//           monthSales: d.sales.this_month,
//           outstanding: d.sales.outstanding,
//         });
//         setFollowupStats({
//           today:d.lead_followups.today,
//           tomorrow:d.lead_followups.tomorrow,
//           pending:d.lead_followups.pending,
//           upcoming:d.lead_followups.upcoming,
//         })
//       }
//     } catch (err) {
//       console.error(err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   /* ================= FETCH LINE CHART ================= */
//   const fetchRetailerLineChart = async () => {
//     try {
//       const params = {};
//       if (locationMode === "state") params.state = userState;
//       if (locationMode === "district") params.district = userDistrict;

//       const res = await axios.get(
//         `${BASE_URL}company/employee-retailer-line-chart`,
//         { params }
//       );

//       if (res.data?.success) {
//         setRetailerLineStats(res.data.data);
//       }
//     } catch (err) {
//       console.error("Line chart API failed", err);
//     }
//   };


// const fetchSalesChart = async () => {
//   try {
//     setSalesChartLoading(true);

//     const params = {
//       type: salesChartType,
//     };

//     if (salesChartType === "day" && selectedMonth) {
//       const { from_date, to_date } = getMonthRange(selectedMonth);
//       params.from_date = from_date;
//       params.to_date = to_date;
//     }

//     if (locationMode === "state" && userState) {
//       params.states = userState;
//     }

//     if (locationMode === "district" && userDistrict) {
//       params.districts = userDistrict;
//     }

//     const res = await axios.get(
//       `${BASE_URL}company/employee-sales-chart`,
//       { params }
//     );

//     if (res.data?.success) {
//       const { labels = [], datasets = [] } = res.data.chart;

//       let formatted = [];

//       // ================= DAY WISE =================

//       if (salesChartType === "day") {
//         const baseMonth = selectedMonth || new Date(); // 👈 KEY FIX
//         const allDates = getAllDatesOfMonth(baseMonth);

//         const apiDataMap = {};
//         labels.forEach((label, index) => {
//           apiDataMap[label] = {
//             sales: datasets[0]?.data?.[index] || 0,
//             collection: datasets[1]?.data?.[index] || 0,
//           };
//         });

//         formatted = allDates.map((date) => ({
//           label: formatDayLabel(date),
//           sales: apiDataMap[date]?.sales || 0,
//           collection: apiDataMap[date]?.collection || 0,
//         }));
//       }

//       // ================= MONTH WISE =================
//       if (salesChartType === "month") {
//         const allMonths = getAllMonthsOfYear(new Date().getFullYear());

//         const apiDataMap = {};
//         labels.forEach((label, index) => {
//           apiDataMap[label] = {
//             sales: datasets[0]?.data?.[index] || 0,
//             collection: datasets[1]?.data?.[index] || 0,
//           };
//         });

//         formatted = allMonths.map((month) => ({
//           label: formatMonthLabel(month), // Jan, Feb, Mar...
//           sales: apiDataMap[month]?.sales || 0,
//           collection: apiDataMap[month]?.collection || 0,
//         }));
//       }


//       setSalesChartData(formatted);
//     }

//   } catch (err) {
//     console.error("Sales chart API failed", err);
//   } finally {
//     setSalesChartLoading(false);
//   }
// };

// useEffect(() => {
//   if (!locationMode) return;

//   fetchEmployeeDashboard();
//   fetchRetailerLineChart();
//   fetchSalesChart();
// }, [locationMode, userState, userDistrict, salesChartType,selectedMonth]);

//   return (
//     <div className="space-y-4">

//        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
//        <KpiCard
//         label="Assigned Retailers"
//         value={loading ? <InlineLoader /> : kpis.totalAssigned}
//         onClick={() =>
//           navigate("/oprretail", {
//             state: { filter: "assigned" },
//           })
//         }
//       />

//       <KpiCard
//         label="Connected Retailers"
//         value={
//           loading ? <InlineLoader width="w-12" /> : `${kpis.connected}/${kpis.totalAssigned}`
//         }
//         subLabel={
//           loading ? <InlineLoader /> : `${kpis.notConnected} not connected`
//         }
//         onClick={() =>
//           navigate("/oprretail", {
//             state: { filter: "connected" },
//           })
//         }
//       />

//       <KpiCard
//         label="Using Our Product"
//         value={loading ? <InlineLoader /> : kpis.usingProduct}
//         subLabel={
//           loading ? <InlineLoader /> : `${kpis.notUsingProduct} not using`
//         }
//         onClick={() =>
//           navigate("/oprretail", {
//             state: { filter: "using" },
//           })
//         }
//       />

//         <KpiCard
//           label="This Month Sales"
//           value={
//             loading ? (
//               <InlineLoader width="w-14" />
//             ) : (
//               `₹${(kpis.monthSales / 1000).toFixed(1)}k`
//             )
//           }
//         />

//         <KpiCard
//           label="Total Outstanding"
//           value={
//             loading ? (
//               <InlineLoader width="w-14" />
//             ) : (
//               `₹${(kpis.outstanding / 1000).toFixed(1)}k`
//             )
//           }
//           subLabel="Follow up for collections"
//         />
//       </div>

//       {/* Charts */}
//       <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
       
//         <SectionCard title="Sales vs Collection (Assigned Retailers)">
//           {/* Tabs */}
//           <div className="flex gap-2 mb-3">

//             {salesChartType === "day" && (
//               <div className="mb-3">
//                 <DatePicker
//                   selected={selectedMonth}
//                   onChange={(date) => setSelectedMonth(date)}
//                   dateFormat="MMMM, yyyy"
//                   showMonthYearPicker
//                   placeholderText="Select month"
//                   className="border rounded px-3 py-1 text-sm cursor-pointer"
//                 />
//               </div>
//             )}

//             <button
//               onClick={() => setSalesChartType("day")}
//               className={`px-2 py-1 rounded text-sm ${
//                 salesChartType === "day"
//                   ? "bg-blue-600 text-white"
//                   : "bg-slate-100 text-slate-600"
//               }`}
//             >
//               Day
//             </button>

//             <button
//               onClick={() => setSalesChartType("month")}
//               className={`px-2 py-1 rounded text-sm ${
//                 salesChartType === "month"
//                   ? "bg-blue-600 text-white"
//                   : "bg-slate-100 text-slate-600"
//               }`}
//             >
//               Month
//             </button>
//           </div>

//           <div className="h-64">
//             {salesChartLoading ? (
//               <div className="h-full flex items-center justify-center">
//                 <ValueLoader />
//               </div>
//             ) : (
//               <ResponsiveContainer width="100%" height="100%">
//                 <BarChart data={salesChartData}>
//                   <CartesianGrid strokeDasharray="3 3" />
//                   <XAxis dataKey="label" />
//                   <YAxis />
//                   <Tooltip />
//                   <Legend />
//                   <Bar dataKey="sales" fill="#2563eb" name="Total Sales" />
//                   <Bar dataKey="collection" fill="#10b981" name="Paid Sales" />
//                 </BarChart>
//               </ResponsiveContainer>
//             )}
//           </div>
//         </SectionCard>

//           <div className="flex flex-col gap-4 h-full">
//           <SectionCard title="Retailer Status Snapshot">
//           <div className="h-40 flex flex-col justify-between">
//             <div className="space-y-2 text-sm">
//               <StatusRow
//                 label="Active retailers"
//                 value={retailerLineStats.active.count}
//                 total={retailerLineStats.active.total}
//               />
//               <StatusRow
//                 label="Connected retailers"
//                 value={retailerLineStats.connected.count}
//                 total={retailerLineStats.connected.total}
//               />
//               <StatusRow
//                 label="Using product"
//                 value={retailerLineStats.using.count}
//                 total={retailerLineStats.using.total}
//               />
//             </div>
//           </div>
//         </SectionCard>

//         <SectionCard title="Follow-up Snapshot">
//           <div className="grid grid-cols-2 gap-3">

//             <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
//               <div className="text-xs text-slate-500 mb-1">
//                 Today's Follow-ups
//               </div>
//               <div className="text-2xl font-bold text-blue-600">
//                 {followupStats.today}
//               </div>
//             </div>

//             <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-4">
//               <div className="text-xs text-slate-500 mb-1">
//                 Tomorrow
//               </div>
//               <div className="text-2xl font-bold text-emerald-600">
//                 {followupStats.tomorrow}
//               </div>
//             </div>

//             <div className="rounded-xl border border-amber-100 bg-amber-50 p-4">
//               <div className="text-xs text-slate-500 mb-1">
//                 Pending
//               </div>
//               <div className="text-2xl font-bold text-amber-600">
//                 {followupStats.pending}
//               </div>
//             </div>

//             <div className="rounded-xl border border-violet-100 bg-violet-50 p-4">
//               <div className="text-xs text-slate-500 mb-1">
//                 Upcoming
//               </div>
//               <div className="text-2xl font-bold text-violet-600">
//                 {followupStats.upcoming}
//               </div>
//             </div>

//           </div>
//         </SectionCard>
//         </div>
//       </div>
//     </div>
//   );
// }
